import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertTriangle, Trash2, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface DeleteAccountSectionProps {
  portal: 'seek' | 'hire';
  onDelete: () => Promise<void>;
  onSignOut: () => void;
}

export function DeleteAccountSection({ portal, onDelete, onSignOut }: DeleteAccountSectionProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const portalName = portal === 'seek' ? 'ForkSeek' : 'ForkHire';
  const cardDescription = portal === 'hire'
    ? t('common.deleteAccount.hireCardDescription')
    : t('common.deleteAccount.seekCardDescription');
  const dialogDescription = portal === 'hire'
    ? t('common.deleteAccount.hireDialogDescription')
    : t('common.deleteAccount.seekDialogDescription');

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') return;
    setDeleting(true);
    try {
      await onDelete();
      toast({ title: t('common.deleteAccount.requestReceived'), description: t('common.deleteAccount.requestReceivedDescription', { portal: portalName }) });
      setOpen(false);
      onSignOut();
    } catch (err: unknown) {
      toast({ title: t('common.deleteAccount.deletionFailed'), description: err instanceof Error ? err.message : t('common.tryAgain'), variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Card className="border-destructive/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="w-5 h-5" />
            {t('common.deleteAccount.title')}
          </CardTitle>
          <CardDescription>
            {cardDescription}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="destructive"
            onClick={() => setOpen(true)}
            className="rounded-xl"
          >
            <Trash2 className="w-4 h-4 me-2" />
            {t('common.deleteAccount.deleteMyAccount', { portal: portalName })}
          </Button>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-xl">
          <DialogHeader>
            <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-2">
              <AlertTriangle className="w-6 h-6 text-destructive" />
            </div>
            <DialogTitle className="text-center font-display">{t('common.deleteAccount.dialogTitle', { portal: portalName })}</DialogTitle>
            <DialogDescription className="text-center">
              {dialogDescription}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              {t('common.deleteAccount.typeToConfirmBefore')} <strong className="text-foreground">DELETE</strong> {t('common.deleteAccount.typeToConfirmAfter')}
            </p>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              className="h-11 rounded-xl font-mono"
              autoFocus
            />
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)} disabled={deleting}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              className="rounded-xl"
              onClick={handleDelete}
              disabled={confirmText !== 'DELETE' || deleting}
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : null}
              {deleting ? t('common.submitting') : t('common.deleteAccount.submitRequest')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
